import React from 'react';
import {
  ScheduledItemType,
  useAnswerDataSurveyBhbMutation,
  useAnswerSurveyMutation,
  useGetSurveyInstanceLazyQuery,
  useAnswerAnytimeSurveyForSubjectMutation,
  useGetAvailableSurveysQuery,
  GetAvailableSurveysDocument,
} from '@fdha/graphql-api-patient';
import { useDialog, useSnackbar } from '@fdha/web-ui-library';
import { Steps } from '@pages';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  buildBhbDataSurveyInputPayload,
  buildSurveyViews,
  getAnswers,
} from '@utils';
import { useAnalytics } from '@fdha/common-hooks';

import { SurveyData } from '../steps/Steps';

import BHBSurveyDialog from './BHBSurveyDialog';

const SurveySteps = () => {
  const { showSnackbarV2 } = useSnackbar();
  const { openDialogV2, closeDialog } = useDialog();
  const { analyticsClient } = useAnalytics();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const instanceId = params?.id || '';
  const { isAnytimeSurvey, surveyData: anytimeSurveyData } =
    location.state || {};

  const [answerSurvey] = useAnswerSurveyMutation();

  const [answerDataSurveyBhb] = useAnswerDataSurveyBhbMutation();

  const [answerAnytimeSurvey] = useAnswerAnytimeSurveyForSubjectMutation();

  const [
    getSurvey,
    { data: surveyInstanceData, loading: surveyInstanceLoading },
  ] = useGetSurveyInstanceLazyQuery({
    variables: { instanceId, showDataSurveys: true },
    fetchPolicy: 'cache-and-network',
  });

  const { data: surveysData } = useGetAvailableSurveysQuery({
    variables: { showDataSurveys: true, version: 5 },
    skip: isAnytimeSurvey,
  });

  const requiredSurveys =
    surveysData?.availableSurveys.edges.map((edge) => edge.node) || [];

  const nextSurvey = isAnytimeSurvey
    ? null
    : requiredSurveys.find((survey) => survey.id !== instanceId) || null;

  const isBHB = useMemo(
    () =>
      surveyInstanceData?.surveyInstance?.instanceType ===
      ScheduledItemType.Bhb,
    [surveyInstanceData?.surveyInstance?.instanceType]
  );
  const dueAtDate = surveyInstanceData?.surveyInstance?.due_at || '';

  useEffect(() => {
    if (!isAnytimeSurvey) {
      getSurvey();
    }
  }, [getSurvey, isAnytimeSurvey]);

  const handleAnswerSurvey = async (values: any) => {
    const answers = getAnswers(values);

    await answerSurvey({
      variables: {
        instanceId,
        answers,
      },
      refetchQueries: [GetAvailableSurveysDocument],
    });
  };

  const handleAnswerDataSurvey = async (values: any) => {
    const bhbValues = values.bhb;
    const patientBhbAnswer = buildBhbDataSurveyInputPayload(
      instanceId,
      bhbValues.value,
      bhbValues.time,
      bhbValues.dayPeriod,
      dueAtDate
    );
    await answerDataSurveyBhb({
      variables: {
        bhbData: patientBhbAnswer,
      },
    });
    closeDialog();
  };

  const openBHBDialog = (values: any) => {
    openDialogV2({
      title: 'Please review this information is correct before submitting',
      content: <BHBSurveyDialog values={values} date={dueAtDate} />,
      cancelButtonLabel: 'Back',
      confirmButtonLabel: 'Submit',
      handleConfirm: () => handleSubmit(values),
      i18nKeyTitle: 'surveys:bhbSurvey.confirmationDialog.title',
    });
  };

  const navigateAfterSurveyCompletion = () => {
    if (nextSurvey) {
      navigate(`/surveys/${nextSurvey.id}`);
    } else {
      navigate('/surveys', { replace: true });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      let segmentTrackingLog = {};

      if (isAnytimeSurvey) {
        await answerAnytimeSurvey({
          variables: {
            surveyId: anytimeSurveyData?.surveyId,
            answers: getAnswers(values),
            respondedAt: new Date().toISOString(),
            respondedAtTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });
        segmentTrackingLog = {
          id: instanceId,
          name: anytimeSurveyData?.name,
          type: 'ANYTIME',
        };
      } else if (isBHB) {
        await handleAnswerDataSurvey(values);
        segmentTrackingLog = {
          id: instanceId,
          name: 'BHB',
          type: ScheduledItemType.Bhb,
        };
      } else {
        await handleAnswerSurvey(values);
        segmentTrackingLog = {
          id: instanceId,
          name: surveyInstanceData?.surveyInstance?.name,
          type: surveyInstanceData?.surveyInstance?.instanceType,
        };
      }

      analyticsClient?.track('Survey Completed', segmentTrackingLog);

      navigateAfterSurveyCompletion();
      showSnackbarV2({
        severity: 'success',
        message: 'Survey completed. Great job!',
        i18nKey: 'surveys:snackbar.surveyCompleted',
      });
    } catch (error) {
      console.error(error);
      showSnackbarV2({
        severity: 'error',
        message: 'Error to answer survey',
        i18nKey: 'surveys:snackbar.errorAnswerSurvey',
      });
    }
  };

  const views = useMemo(() => {
    if (isAnytimeSurvey) {
      return buildSurveyViews(anytimeSurveyData);
    }
    if (!surveyInstanceData?.surveyInstance) {
      return;
    }
    return buildSurveyViews(surveyInstanceData.surveyInstance);
  }, [isAnytimeSurvey, anytimeSurveyData, surveyInstanceData?.surveyInstance]);

  const surveyData = useMemo(() => {
    let data: SurveyData = isAnytimeSurvey
      ? {
          title: anytimeSurveyData?.name,
          date: undefined,
        }
      : {
          title: surveyInstanceData?.surveyInstance?.name,
          date: surveyInstanceData?.surveyInstance?.due_at
            ? Number(surveyInstanceData.surveyInstance.due_at)
            : undefined,
          i18nKeySubtitle: surveyInstanceData?.surveyInstance?.due_at
            ? 'surveys:steps.subtitle'
            : '',
          i18nSubtitleParams: {
            date: surveyInstanceData?.surveyInstance?.due_at || '',
          },
        };

    return data;
  }, [isAnytimeSurvey, anytimeSurveyData, surveyInstanceData?.surveyInstance]);

  return (
    <Steps
      key={instanceId}
      surveyData={surveyData}
      views={views}
      loading={isAnytimeSurvey ? false : surveyInstanceLoading}
      handleSubmit={(values) =>
        isBHB ? openBHBDialog(values) : handleSubmit(values)
      }
      hasNextSurvey={!!nextSurvey}
    />
  );
};

export default SurveySteps;
